import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  ErrorModel,
  GlobalMessageService,
  GlobalMessageType,
  HttpErrorModel,
  RouterState,
  RoutingService,
} from '@spartacus/core';
import { CustomFormValidators } from '@spartacus/storefront';
import { ResetPasswordComponentService } from '@spartacus/user/profile/components';
import { UserPasswordFacade } from '@spartacus/user/profile/root';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CustomValidators } from 'src/app/spartacus/custom/shared/utils/validators/custom-validators';

@Injectable({ providedIn: 'root' })
export class CustomResetPasswordComponentService extends ResetPasswordComponentService {
  protected susbscriptions: Subscription = new Subscription();

  constructor(
    protected userPasswordService: UserPasswordFacade,
    protected routingService: RoutingService,
    protected globalMessage: GlobalMessageService
  ) {
    super(userPasswordService, routingService, globalMessage);
  }

  protected busy$ = new BehaviorSubject(false);

  isUpdating$ = this.busy$.pipe(
    tap((state) => (state === true ? this.form.disable() : this.form.enable()))
  );

  resetToken$: Observable<string> = this.routingService
    .getRouterState()
    .pipe(
      map((routerState: RouterState) => routerState.state.queryParams['token'])
    );

  form: UntypedFormGroup = new UntypedFormGroup(
    {
      password: new UntypedFormControl('', CustomValidators.passwordValidators),
      passwordConfirm: new UntypedFormControl('', Validators.required),
    },
    {
      validators: CustomFormValidators.passwordsMustMatch(
        'password',
        'passwordConfirm'
      ),
    }
  );

  /**
   * Resets the password by the given token.
   *
   * The token has been provided during the request password flow.
   * The token is not validated on the client.
   */
  resetPassword(token: string): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.busy$.next(true);

    const password = (this.form.get('password') as UntypedFormControl).value;

    this.susbscriptions.add(this.userPasswordService.reset(token, password).subscribe({
      next: () => this.onSuccess(),
      error: (error: Error) => this.onError(error),
    }));
  }

  protected onSuccess(): void {
    this.globalMessage.add(
      { key: 'forgottenPassword.passwordResetSuccess' },
      GlobalMessageType.MSG_TYPE_CONFIRMATION
    );
    this.busy$.next(false);
    this.form.reset();
    this.redirect();
  }

  protected onError(error: Error): void {
    this.busy$.next(false);
    ((error as HttpErrorModel)?.details ?? []).forEach((err: ErrorModel) => {
      if (err.message) {
        this.globalMessage.add(
          { raw: err.message },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      }
    });
  }

  /**
   * Redirects the user to the login page.
   */
  protected redirect() {
    this.routingService.go({ cxRoute: 'login' });
  }

  ngOnDestroy(): void {
    if (this.susbscriptions) {
      this.susbscriptions.unsubscribe();
    }
  }
}
